import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectDatabaseApi, type ProjectDatabaseView } from "@/lib/api/project-database";
import { stackKeys } from "./use-cloud";

export const projectDatabaseKeys = {
  project: (teamId: string) => ["project-database", teamId] as const,
};

/** A project's database metadata and the Cloud SQL cost estimate. */
export function useProjectDatabase(teamId: string) {
  return useQuery({
    queryKey: projectDatabaseKeys.project(teamId),
    queryFn: () => projectDatabaseApi.get(teamId),
  });
}

/** Ask for a Cloud SQL database. It rewrites the stack pull request, so the stack is refetched too. */
export function useCreateCloudSqlDatabase(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => projectDatabaseApi.createCloudSql(teamId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: projectDatabaseKeys.project(teamId) }),
        queryClient.invalidateQueries({ queryKey: stackKeys.project(teamId) }),
      ]);
    },
  });
}

/**
 * Paste or replace the connection string. Only the returned metadata goes into
 * the cache: the mutation's variables (the secret) are never stored.
 */
export function useSaveDatabaseConnection(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectionString: string; providerLabel?: string }) =>
      projectDatabaseApi.save(teamId, input),
    // Drop the finished mutation (and the connection string in its variables) right away.
    gcTime: 0,
    onSuccess: (res) => {
      queryClient.setQueryData<ProjectDatabaseView>(projectDatabaseKeys.project(teamId), (old) => ({
        data: res.data,
        estimatedMonthlyUsd: old?.estimatedMonthlyUsd ?? 0,
      }));
    },
  });
}

export function useDisconnectDatabase(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => projectDatabaseApi.disconnect(teamId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: projectDatabaseKeys.project(teamId) }),
  });
}
